import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, Plus, Trash2, CheckCircle } from 'lucide-react';

const DamageInspection = ({ onDamagesChange, onNoDamagesChange }) => {
  const [damages, setDamages] = useState([]);
  const [noDamages, setNoDamages] = useState(false);
  const [addingDamage, setAddingDamage] = useState(false);
  const [currentDamage, setCurrentDamage] = useState({
    position: '',
    description: '',
    severity: 'low',
    notes: ''
  });

  const handleAddDamage = () => {
    if (!currentDamage.position || !currentDamage.description) {
      return;
    }

    const newDamages = [...damages, currentDamage];
    setDamages(newDamages);
    onDamagesChange(newDamages);
    setCurrentDamage({
      position: '',
      description: '',
      severity: 'low',
      notes: ''
    });
    setAddingDamage(false);
    setNoDamages(false);
    onNoDamagesChange(false);
  };

  const handleRemoveDamage = (index) => {
    const newDamages = damages.filter((_, i) => i !== index);
    setDamages(newDamages);
    onDamagesChange(newDamages);
  };

  const handleNoDamagesClick = () => {
    setNoDamages(!noDamages);
    onNoDamagesChange(!noDamages);
    if (!noDamages) {
      setDamages([]);
      onDamagesChange([]);
      setAddingDamage(false);
    }
  };

  const getPositionLabel = (position) => {
    const labels = {
      'front': 'Vorne',
      'rear': 'Hinten',
      'left': 'Links',
      'right': 'Rechts',
      'top': 'Dach',
      'interior': 'Innenraum'
    };
    return labels[position] || position;
  };

  const getSeverityLabel = (severity) => {
    const labels = {
      'low': 'Gering',
      'medium': 'Mittel',
      'high': 'Hoch'
    };
    return labels[severity] || severity;
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'low': return 'bg-yellow-500/20 text-yellow-300 border-yellow-500/50';
      case 'medium': return 'bg-orange-500/20 text-orange-300 border-orange-500/50';
      case 'high': return 'bg-red-500/20 text-red-300 border-red-500/50';
      default: return 'bg-gray-500/20 text-gray-300 border-gray-500/50';
    }
  };

  return (
    <Card className="bg-[#111] border-gray-800">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 text-orange-500" />
          Schadensinspektion
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* No Damages Button */}
        <Button
          type="button"
          onClick={handleNoDamagesClick}
          variant={noDamages ? "default" : "outline"}
          className={`w-full ${noDamages ? 'bg-green-600 hover:bg-green-700' : 'border-gray-600 hover:bg-gray-800'}`}
        >
          <CheckCircle className="h-4 w-4 mr-2" />
          {noDamages ? 'Keine Schäden bestätigt ✓' : 'Keine Schäden vorhanden'}
        </Button>

        {/* Add Damage Button */}
        {!noDamages && (
          <Button
            type="button"
            onClick={() => setAddingDamage(!addingDamage)}
            variant="outline"
            className="w-full border-orange-500/50 text-orange-400 hover:bg-orange-500/10"
            disabled={noDamages}
          >
            <Plus className="h-4 w-4 mr-2" />
            {addingDamage ? 'Abbrechen' : 'Schaden hinzufügen'}
          </Button>
        )}

        {/* Add Damage Form */}
        {addingDamage && !noDamages && (
          <div className="space-y-3 p-4 border border-gray-700 rounded-lg bg-[#0a0a0a]">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm">Position *</Label>
                <Select
                  value={currentDamage.position}
                  onValueChange={(value) => setCurrentDamage({ ...currentDamage, position: value })}
                >
                  <SelectTrigger className="bg-[#1a1a1a] border-gray-700 mt-1">
                    <SelectValue placeholder="Wählen..." />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1a1a1a] border-gray-700">
                    <SelectItem value="front">Vorne</SelectItem>
                    <SelectItem value="rear">Hinten</SelectItem>
                    <SelectItem value="left">Links</SelectItem>
                    <SelectItem value="right">Rechts</SelectItem>
                    <SelectItem value="top">Dach</SelectItem>
                    <SelectItem value="interior">Innenraum</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm">Schweregrad *</Label>
                <Select
                  value={currentDamage.severity}
                  onValueChange={(value) => setCurrentDamage({ ...currentDamage, severity: value })}
                >
                  <SelectTrigger className="bg-[#1a1a1a] border-gray-700 mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1a1a1a] border-gray-700">
                    <SelectItem value="low">Gering</SelectItem>
                    <SelectItem value="medium">Mittel</SelectItem>
                    <SelectItem value="high">Hoch</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-sm">Beschreibung *</Label>
              <Textarea
                value={currentDamage.description}
                onChange={(e) => setCurrentDamage({ ...currentDamage, description: e.target.value })}
                placeholder="Beschreiben Sie den Schaden..."
                className="bg-[#1a1a1a] border-gray-700 mt-1 min-h-[70px]"
              />
            </div>
            <div>
              <Label className="text-sm">Notizen (optional)</Label>
              <Textarea
                value={currentDamage.notes}
                onChange={(e) => setCurrentDamage({ ...currentDamage, notes: e.target.value })}
                placeholder="Zusätzliche Informationen..."
                className="bg-[#1a1a1a] border-gray-700 mt-1 min-h-[50px]"
              />
            </div>
            <Button
              type="button"
              onClick={handleAddDamage}
              className="w-full bg-orange-600 hover:bg-orange-700"
              disabled={!currentDamage.position || !currentDamage.description}
            >
              Schaden hinzufügen
            </Button>
          </div>
        )}

        {/* Damages List */}
        {damages.length > 0 && (
          <div className="space-y-2">
            <Label className="text-sm font-semibold">Gefundene Schäden ({damages.length})</Label>
            {damages.map((damage, index) => (
              <div key={index} className="flex items-start justify-between p-3 bg-[#0a0a0a] border border-gray-700 rounded-lg">
                <div className="flex-1 space-y-1">
                  <div className="flex items-center gap-2">
                    <Badge className={getSeverityColor(damage.severity)}>
                      {getSeverityLabel(damage.severity)}
                    </Badge>
                    <Badge variant="outline" className="border-gray-600 text-gray-300">
                      {getPositionLabel(damage.position)}
                    </Badge>
                  </div>
                  <p className="text-sm">{damage.description}</p>
                  {damage.notes && (
                    <p className="text-xs text-gray-400">{damage.notes}</p>
                  )}
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => handleRemoveDamage(index)}
                  className="text-red-400 hover:text-red-300 hover:bg-red-950 ml-2"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        )}

        {noDamages && (
          <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg text-center">
            <CheckCircle className="h-8 w-8 mx-auto mb-2 text-green-500" />
            <p className="text-green-400 font-medium">Fahrzeug ist in einwandfreiem Zustand</p>
            <p className="text-sm text-gray-400 mt-1">Keine Schäden dokumentiert</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default DamageInspection;
